import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";

const VARS = "{cliente} {servico} {mes} {data} {empresa} {responsavel}";
const VISIT_VARS = "{cliente} {servico} {dia_semana} {data} {empresa} {responsavel} {telefone_empresa}";
const MODES = {
  service_lead: "Pela antecedência de cada serviço (Catálogo)",
  monthly: "No planejamento do dia 25 (mês anterior)",
  days_before: "Mesmo número de dias para todos",
};

const Tpl = ({ id, label, value, onChange, vars, rows = 4 }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Textarea id={id} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className="bg-slate-950" data-testid={`${id.replace(/_/g, "-")}-input`} />
    <p className="text-xs text-slate-500">Variáveis: <span className="font-mono">{vars}</span></p>
  </div>
);

export const MessagingDialog = ({ open, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) api.get("/settings/messaging").then((r) => setF(r.data)).catch((e) => toast.error(errorMessage(e))); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/settings/messaging", { ...f, days_before: Number(f.days_before) });
      toast.success("Configuração de mensagens salva.");
      onSaved?.();
      onClose();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const mode = (m) => (
    <button type="button" onClick={() => setF({ ...f, send_mode: m })} data-testid={`messaging-mode-${m}`}
      className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors duration-150 ${f?.send_mode === m ? "border-slate-100 bg-slate-100 text-slate-900" : "border-slate-700 text-slate-300"}`}>
      {MODES[m]}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-slate-700 bg-slate-900 sm:max-w-lg" data-testid="messaging-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">Mensagens aos clientes</DialogTitle>
          <DialogDescription>Escolha um canal por vez para os envios automáticos. Os botões de WhatsApp das listas continuam abrindo a conversa com a mensagem pronta.</DialogDescription>
        </DialogHeader>
        {!f ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-700 bg-slate-950/50 p-3">
              <Label htmlFor="msg_email">Enviar lembretes de retorno automaticamente</Label>
              <Switch id="msg_email" checked={f.email_enabled} onCheckedChange={(email_enabled) => setF({ ...f, email_enabled })} data-testid="messaging-email-switch" />
            </div>
            <div className="space-y-1.5">
              <Label>Canal de envio (lembretes e avisos de agendamento)</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                {[["email", "E-mail"], ["whatsapp", "WhatsApp Business"]].map(([k, l]) => (
                  <button key={k} type="button" disabled={k === "whatsapp" && !f.whatsapp_ready} onClick={() => setF({ ...f, channel: k })} data-testid={`messaging-channel-${k}`}
                    className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors duration-150 disabled:opacity-40 ${f.channel === k ? "border-slate-100 bg-slate-100 text-slate-900" : "border-slate-700 text-slate-300"}`}>{l}</button>
                ))}
              </div>
              {!f.whatsapp_ready && <p className="text-xs text-slate-500">Para usar o WhatsApp, configure antes em Mensagens &gt; WhatsApp Business.</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Quando entrar em contato</Label>
              <div className="grid gap-2 sm:grid-cols-3">{mode("service_lead")}{mode("monthly")}{mode("days_before")}</div>
              {f.send_mode === "days_before" && (
                <div className="flex items-center gap-2 text-sm"><Input type="number" min={1} max={90} value={f.days_before} onChange={(e) => setF({ ...f, days_before: e.target.value })} className="h-9 w-20 bg-slate-950" data-testid="messaging-days-input" /> dias antes</div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="msg_subject">Assunto do e-mail</Label>
              <Input id="msg_subject" value={f.email_subject} onChange={(e) => setF({ ...f, email_subject: e.target.value })} className="h-10 bg-slate-950" data-testid="messaging-subject-input" />
            </div>
            <Tpl id="messaging_template" label="Lembrete de retorno (e-mail e WhatsApp do Planejamento)" value={f.template} onChange={(template) => setF({ ...f, template })} vars={VARS} rows={5} />
            <Tpl id="messaging_notice" label="Aviso de agendamento (e-mail, vai com o botão Confirmar visita)" value={f.notice_template} onChange={(notice_template) => setF({ ...f, notice_template })} vars={VISIT_VARS} />
            <Tpl id="messaging_visit" label="Confirmação de visita (WhatsApp nos serviços agendados e na Rota)" value={f.visit_template} onChange={(visit_template) => setF({ ...f, visit_template })} vars={VISIT_VARS} />
            <Tpl id="messaging_greeting" label="Saudação (WhatsApp da ficha e da lista de clientes)" value={f.greeting_template} onChange={(greeting_template) => setF({ ...f, greeting_template })} vars="{cliente} {empresa} {responsavel} {telefone_empresa}" rows={2} />
            <p className="text-xs text-slate-500">Clientes marcados como “não aceita e-mails” ou inativos não recebem. Cada retorno recebe no máximo um lembrete automático. No WhatsApp Business o texto segue o modelo aprovado na Meta.</p>
            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={onClose} data-testid="messaging-cancel">Cancelar</Button>
              <Button type="submit" disabled={busy} className="rounded-full font-semibold" data-testid="messaging-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
